import cn from 'classnames';
import { useTranslation } from 'react-i18next';
import { IAdminPayment, TSortPaymentHistoryByHeaderTitle } from 'types';
import { AdminListHeaderCell } from '../AdminListHeaderCell';
import { StyledAdminPaymentHistoryListHeader } from './styles';
import { AdminCalendar } from '../AdminCalendar';
import { AdminFilterSelect } from '../AdminFilterSelect';

interface IPaymentHistoryListHeaderProps {
  children?: React.ReactNode;
  onSort: (sortBy: TSortPaymentHistoryByHeaderTitle) => void;
  sortBy: string | null;
  sortDir: 'ASC' | 'DESC' | 'NONE';
  onFilterByDate: (filterByDate: {
    filterBy: keyof IAdminPayment;
    fromDate: Date;
    toDate: Date;
  }) => void;
  onFilterByValue: (filterValue: string) => void;
}

type FieldType = {
  key: TSortPaymentHistoryByHeaderTitle;
  sortable?: boolean;
  filterable?: boolean;
  filterWithDate?: boolean;
  options?: string[];
};

export const headerFields: FieldType[] = [
  { key: 'id', sortable: true },
  { key: 'hashId' },
  { key: 'status', filterable: true, options: ['-', 'CONFIRMED', 'UNCONFIRMED'] },
  { key: 'createdAt', filterable: true, filterWithDate: true },
  { key: 'updatedAt', filterable: true, filterWithDate: true },
];

export const AdminPaymentHistoryListHeader = ({
  onSort,
  sortBy,
  sortDir,
  onFilterByDate,
  onFilterByValue,
}: IPaymentHistoryListHeaderProps) => {
  const { t } = useTranslation();

  const renderFilter = (
    key: TSortPaymentHistoryByHeaderTitle,
    withDate?: boolean,
    options?: string[],
  ) => {
    return withDate
      ? () => (
          <AdminCalendar
            onChange={(dateRange: { fromDate: Date; toDate: Date }) =>
              onFilterByDate({
                filterBy: key,
                fromDate: dateRange.fromDate,
                toDate: dateRange.toDate,
              })
            }
          />
        )
      : options
        ? () => (
            <AdminFilterSelect
              onSelect={(value: string) => onFilterByValue(value)}
              filterName={key}
              optionsList={options}
            />
          )
        : undefined;
  };

  return (
    <StyledAdminPaymentHistoryListHeader>
      {headerFields.map(({ key, sortable, filterable, filterWithDate, options }) => (
        <div key={key} className={cn('admin-list-header-cell-container', key.toLowerCase())}>
          <AdminListHeaderCell
            fieldKey={key}
            filter={filterable}
            sort={sortable}
            onSort={sortable ? () => onSort(key) : undefined}
            onFilter={filterable ? renderFilter(key, filterWithDate, options) : undefined}
            isActive={sortBy === key}
            sortDir={sortBy === key ? sortDir : 'NONE'}
          >
            {t(`adminPage.editPaymentHistory.listHeaders.${key}`)}
          </AdminListHeaderCell>
        </div>
      ))}
    </StyledAdminPaymentHistoryListHeader>
  );
};
